/**
 * Transcript export (spec §18).
 *
 * Pure: every formatter here is a function over `TranscriptEntry[]`, with no
 * DOM access at all. That is what makes the SRT/VTT output testable in Node and
 * reusable from a Worker. The browser side — the download click and the
 * clipboard — lives in `./download.ts`.
 *
 * SRT and VTT are first-class outputs because live subtitles are a headline
 * feature: a conversation transcript is much less useful if the next step is
 * "now paste this into a subtitle tool".
 */

import {
  formatSrtTimestamp,
  formatVttTimestamp,
  type Transcript,
  type TranscriptEntry,
  type TranscriptExportFormat,
} from "@shared/types/transcript";
import { languageLabel } from "@shared/languages";

export interface ExportOptions {
  readonly format: TranscriptExportFormat;
  /** Include the source language beside each line. */
  readonly bilingual: boolean;
  /** Include speaker labels. */
  readonly includeSpeakers: boolean;
  /** Include a header block with session metadata. */
  readonly includeHeader: boolean;
  /** Only the target language — what a subtitle track wants. */
  readonly targetOnly: boolean;
}

export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  format: "txt",
  bilingual: true,
  includeSpeakers: true,
  includeHeader: true,
  targetOnly: false,
};

export const EXPORT_MIME: Readonly<Record<TranscriptExportFormat, string>> = {
  txt: "text/plain;charset=utf-8",
  md: "text/markdown;charset=utf-8",
  json: "application/json;charset=utf-8",
  srt: "application/x-subrip;charset=utf-8",
  vtt: "text/vtt;charset=utf-8",
};

const EXTENSION: Readonly<Record<TranscriptExportFormat, string>> = {
  txt: "txt",
  md: "md",
  json: "json",
  srt: "srt",
  vtt: "vtt",
};

/** Cues shorter than this are unreadable and break some players. */
const MIN_CUE_MS = 400;
/** Two cues overlapping by more than this are confusing; they get nudged. */
const CUE_OVERLAP_MS = 80;

export interface Cue {
  readonly entry: TranscriptEntry;
  readonly startMs: number;
  readonly endMs: number;
}

export function exportTranscript(transcript: Transcript, options: ExportOptions): string {
  const entries = transcript.entries.filter((entry) => entry.status !== "failed");
  switch (options.format) {
    case "json":
      return exportJson(transcript);
    case "srt":
      return exportSrt(entries, options);
    case "vtt":
      return exportVtt(entries, options);
    case "md":
      return exportMarkdown(transcript, entries, options);
    default:
      return exportPlainText(transcript, entries, options);
  }
}

export function filenameFor(transcript: Transcript, format: TranscriptExportFormat): string {
  const stamp = new Date(transcript.createdAt || Date.now()).toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return `dream-tongue-${stamp}.${EXTENSION[format]}`;
}

/**
 * Re-time a transcript into non-overlapping, readable cues.
 *
 * The client's `startMs`/`endMs` come from utterance boundaries, which overlap
 * when one speaker interrupts another. An overlapping subtitle is rejected
 * outright by several players, so cues are clamped sequentially and padded to a
 * readable minimum. The first cue is never nudged — a file that starts at zero
 * should start at zero.
 */
export function normalizeCues(entries: readonly TranscriptEntry[]): Cue[] {
  const cues: Cue[] = [];
  let previousEnd: number | null = null;

  for (const entry of entries) {
    if (entry.status === "failed") continue;
    let startMs = Math.max(0, Math.round(entry.startMs));
    let endMs = Math.max(startMs + MIN_CUE_MS, Math.round(entry.endMs));

    if (previousEnd !== null && startMs < previousEnd + CUE_OVERLAP_MS) {
      startMs = previousEnd + CUE_OVERLAP_MS;
      endMs = Math.max(startMs + MIN_CUE_MS, endMs);
    }

    cues.push({ entry, startMs, endMs });
    previousEnd = endMs;
  }

  return cues;
}

// ---------------------------------------------------------------------------
//  Formatters
// ---------------------------------------------------------------------------

function header(transcript: Transcript, options: ExportOptions): string[] {
  if (!options.includeHeader) return [];
  return [
    "Dream Tongue — conversation transcript",
    `Started:    ${new Date(transcript.createdAt || Date.now()).toLocaleString()}`,
    `Source:     ${transcript.sourceLanguage === "auto" ? "auto-detected" : languageLabel(transcript.sourceLanguage)}`,
    `Target:     ${languageLabel(transcript.targetLanguage)}`,
    `Utterances: ${transcript.entries.length}`,
    ...(transcript.transcriptId ? [`Transcript: ${transcript.transcriptId}`] : []),
    "",
    "─".repeat(60),
    "",
  ];
}

function exportPlainText(transcript: Transcript, entries: readonly TranscriptEntry[], options: ExportOptions): string {
  const lines = header(transcript, options);
  for (const entry of entries) {
    const who = options.includeSpeakers ? `${entry.speaker}  ` : "";
    const time = `[${entry.timestamp}]`;
    lines.push(`${time} ${who}${entry.sourceText}`);
    if (options.bilingual && !options.targetOnly && entry.translatedText !== entry.sourceText) {
      lines.push(`${" ".repeat(time.length + who.length + 2)}→ ${entry.translatedText}`);
    }
    lines.push("");
  }
  return `${lines.join("\n").trimEnd()}\n`;
}

function exportMarkdown(transcript: Transcript, entries: readonly TranscriptEntry[], options: ExportOptions): string {
  const lines = [...header(transcript, options), "# Dream Tongue transcript", ""];
  for (const entry of entries) {
    const who = options.includeSpeakers ? `**${entry.speaker}** · ` : "";
    lines.push(`### ${entry.timestamp} — ${who}${languageLabel(entry.sourceLanguage)}`, "");
    if (!options.targetOnly) lines.push(`> ${entry.sourceText}`, "");
    lines.push(entry.translatedText, "");
  }
  return `${lines.join("\n").trimEnd()}\n`;
}

function exportJson(transcript: Transcript): string {
  return `${JSON.stringify(
    {
      version: 1,
      generator: "dream-tongue-convo",
      sessionId: transcript.sessionId,
      transcriptId: transcript.transcriptId,
      createdAt: new Date(transcript.createdAt || Date.now()).toISOString(),
      updatedAt: new Date(transcript.updatedAt || Date.now()).toISOString(),
      sourceLanguage: transcript.sourceLanguage,
      targetLanguage: transcript.targetLanguage,
      entryCount: transcript.entries.length,
      entries: transcript.entries.map((entry) => ({
        id: entry.id,
        index: entry.index,
        timestamp: entry.timestamp,
        startedAt: new Date(entry.startedAt).toISOString(),
        endedAt: new Date(entry.endedAt).toISOString(),
        startMs: Math.round(entry.startMs),
        endMs: Math.round(entry.endMs),
        speaker: entry.speaker,
        sourceLanguage: entry.sourceLanguage,
        sourceText: entry.sourceText,
        targetLanguage: entry.targetLanguage,
        translatedText: entry.translatedText,
        detectionConfidence: entry.detectionConfidence,
        transcriptionMs: Math.round(entry.transcriptionMs),
        translationMs: Math.round(entry.translationMs),
      })),
    },
    null,
    2,
  )}\n`;
}

function exportSrt(entries: readonly TranscriptEntry[], options: ExportOptions): string {
  const blocks: string[] = [];
  normalizeCues(entries).forEach((cue, index) => {
    const text = options.targetOnly
      ? cue.entry.translatedText
      : options.bilingual
        ? `${cue.entry.sourceText}\n${cue.entry.translatedText}`
        : cue.entry.sourceText;
    if (text.trim() === "") return;
    const speaker = options.includeSpeakers ? `<font color="#9b86ff">${cue.entry.speaker}</font> ` : "";
    blocks.push(`${index + 1}\n${formatSrtTimestamp(cue.startMs)} --> ${formatSrtTimestamp(cue.endMs)}\n${speaker}${text}\n`);
  });
  return blocks.join("\n");
}

function exportVtt(entries: readonly TranscriptEntry[], options: ExportOptions): string {
  const cues = normalizeCues(entries);
  const header = ["WEBVTT", ""];
  const first = entries[0];
  if (first) header.push(`NOTE Source language: ${first.sourceLanguage}`, `NOTE Target language: ${first.targetLanguage}`);
  header.push("");

  const blocks: string[] = [];
  for (const cue of cues) {
    const text = options.targetOnly
      ? cue.entry.translatedText
      : options.bilingual
        ? `${cue.entry.sourceText}\n${cue.entry.translatedText}`
        : cue.entry.sourceText;
    if (text.trim() === "") continue;
    // `<v Speaker>` is the standard WebVTT way to attribute a line, and players
    // use it to change the colour or position per speaker.
    const speaker = options.includeSpeakers ? `<v ${cue.entry.speaker}>${cue.entry.sourceText}</v>\n` : "";
    const body = options.bilingual && !options.targetOnly ? `${cue.entry.translatedText}` : "";
    blocks.push(`${cue.entry.id}\n${formatVttTimestamp(cue.startMs)} --> ${formatVttTimestamp(cue.endMs)}\n${speaker}${body}\n`);
  }

  return [...header, ...blocks].join("\n");
}
